/**
 * ReducedTierOverlay - the "Inhalt nicht freigegeben" panel with the Freigabe-Anfrage.
 *
 * Shown instead of the body at tier 'title' / 'metadata' (Berechtigungskonzept V4.0,
 * Bereich 1): the viewer has scope for the customer but not for this mail's content, so
 * decision D2 says show it at metadata level WITH a way to ask. A viewer with no scope at
 * all never gets here - that Eingang does not leave the database (D1, RLS 0113).
 *
 * Controlled on purpose: the Begründung belongs to the mail it was written for, and it is
 * audited, so the OWNER of the reason is the screen that knows which mail is on display.
 * Lived in MailReadingPane until the Bearbeitungsmaske needed the same panel (H2).
 *
 * [Source: docs/posteingang-visibility-decisions.md#d2]
 */
import { Clock, Lock } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import type { VisibilityTier } from '../types';

/**
 * Minimum length of a Freigabe-Anfrage Begründung (Berechtigungskonzept V4.0, Kap 3.3 —
 * "Pflichtfeld Begründung"; matches the api guard REQUEST_REASON_MIN_CHARS).
 */
export const REQUEST_REASON_MIN = 10;

/**
 * Reduced-tier content overlay (PART B / W1): the grey "Inhalt nicht freigegeben"
 * panel that replaces the body at 'title' / 'metadata' tier, with a MANDATORY Begründung
 * field + the "Freigabe anfragen" button (Bereich 1, 3.3 — Pflichtfeld) — or a pending
 * badge once the caller has an open request.
 */
export function ReducedTierOverlay({
  tier,
  pending,
  requestedAt,
  inFlight,
  onRequest,
  reason,
  onReasonChange,
}: {
  tier: VisibilityTier;
  pending: boolean;
  requestedAt?: string | null;
  inFlight?: boolean;
  onRequest?: (reason: string) => void;
  /** Owned by the pane, per mail - a Begründung belongs to the mail it was written for. */
  reason: string;
  onReasonChange: (next: string) => void;
}) {
  const canSubmit = reason.trim().length >= REQUEST_REASON_MIN;

  return (
    <section
      aria-label="Inhalt nicht freigegeben"
      className="flex flex-col items-center gap-3 rounded-md border border-dashed bg-muted/40 p-8 text-center"
    >
      <span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Lock className="size-5" />
      </span>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">Inhalt nicht freigegeben</p>
        <p className="text-xs text-muted-foreground">
          {tier === 'metadata'
            ? 'Du siehst nur Teilnehmer und Datum dieser Nachricht.'
            : 'Du siehst Betreff, Teilnehmer und Datum. Der Inhalt ist gesperrt.'}
        </p>
      </div>
      {pending ? (
        <Badge variant="info" className="gap-1.5">
          <Clock className="size-3" />
          Freigabe angefragt
          {requestedAt ? ` · ${new Date(requestedAt).toLocaleDateString('de-CH')}` : ''}
        </Badge>
      ) : (
        <div className="flex w-full max-w-sm flex-col gap-2">
          {/* Bereich 1 (3.3): the Begründung is a Pflichtfeld — the request button stays
              disabled until it meets the minimum length. */}
          <textarea
            aria-label="Begründung der Freigabe-Anfrage"
            className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-left text-xs"
            rows={2}
            placeholder="Begründung (Pflichtfeld, mind. 10 Zeichen) …"
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {reason.trim().length}/{REQUEST_REASON_MIN}
            </span>
            <Button
              size="sm"
              onClick={() => onRequest?.(reason.trim())}
              disabled={inFlight || !canSubmit}
            >
              <Lock className="size-4" />
              Freigabe anfragen
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
